import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

/** Time the text takes to travel its hidden distance. Long names must not
 *  crawl, short ones must not whip past — so it is speed, not duration. */
const PIXELS_PER_SECOND = 45;
const MIN_DURATION_S = 1.2;

interface MarqueeOnHoverProps {
    children: string;
    className?: string;
}

/**
 * Text that slides to reveal its own overflow while the row it sits in is
 * hovered or focused, then slides back.
 *
 * `xyz:` builder markets have names long enough to truncate in the markets
 * list, and the ticker is the one thing a truncation must not eat: "xyz:CX…"
 * could be any of several markets, and reading it meant opening the market.
 *
 * The animation runs from CSS (`.marquee-on-hover` in global.css, driven by the
 * row's `group` hover) — this measures whether there is anything hidden at all
 * and how far it has to travel. Text that fits is left completely alone: no
 * animation, no attribute, nothing to see.
 */
export const MarqueeOnHover: React.FC<MarqueeOnHoverProps> = ({
    children,
    className,
}) => {
    const textRef = useRef<HTMLSpanElement>(null);
    const [shift, setShift] = useState(0);

    const measure = useCallback(() => {
        const element = textRef.current;
        if (!element) return;
        const parent = element.parentElement;
        if (!parent) return;
        // Sub-pixel widths round against us at these sizes; a hidden pixel or
        // two is not worth animating.
        const hidden = Math.round(element.scrollWidth - parent.clientWidth);
        setShift(hidden > 2 ? hidden : 0);
    }, []);

    // Layout effect: the row is measured before paint, so a name never flashes
    // an animation it does not need.
    useLayoutEffect(() => {
        measure();
        if (typeof ResizeObserver === 'undefined') return;
        // The sidebar is resizable and the sheet reflows on rotation; both
        // change what fits without changing the text.
        const observer = new ResizeObserver(measure);
        const parent = textRef.current?.parentElement;
        if (parent) observer.observe(parent);
        return () => observer.disconnect();
    }, [measure, children]);

    return (
        <span className={cn('marquee-on-hover', className)}>
            <span
                ref={textRef}
                {...(shift > 0 && {
                    'data-overflowing': 'true',
                    style: {
                        '--marquee-shift': `-${shift}px`,
                        '--marquee-duration': `${Math.max(
                            MIN_DURATION_S,
                            shift / PIXELS_PER_SECOND,
                        ).toFixed(2)}s`,
                    } as React.CSSProperties,
                })}
            >
                {children}
            </span>
        </span>
    );
};

export default MarqueeOnHover;
